/**
 * Following view store. Rows are normalised by id so a live `match.add` only re-renders the
 * affected meeting row (each row selects its own slice), never the whole list or layout.
 */
import { createStore } from "zustand/vanilla";
import { subscribeWithSelector } from "zustand/middleware";
import { useStore } from "zustand";
import type { CatchUpLine, Follow, FollowingSettings, FollowingSnapshot, LiveNow, Match, MeetingSummary, Note, Person, PersonCard, TopicStatus, TrackedBill } from "../types";
import { toastStore } from "./toastStore";
import type { MeetingDelta, MeetingSnapshot, OrgDelta, OrgSnapshot } from "../realtime/protocol";

export interface FollowingState {
  follows: Follow[];
  matchesById: Record<string, Match>;
  matchIdsByMeeting: Record<string, string[]>;
  meetingOrder: string[];          // meeting ids, upcoming first then newest past
  catchUp: CatchUpLine[];
  sharedNotes: Note[];
  seenIds: Record<string, true>;
  seq: number;

  // Following view page data (FollowingSnapshot)
  catchUpRange: string;
  meetings: MeetingSummary[];
  othersLabel: string | null;
  people: Record<string, Person>;
  legislation: TrackedBill[];
  person: PersonCard | null;
  topics: TopicStatus[];
  topicsAside: string | null;
  settings: FollowingSettings;
  live: LiveNow | null;            // Live now (spec 11 card 1): banner + LIVE row; speakerId follows the meeting's turns

  hydrate: (snap: OrgSnapshot, meetingOrder: string[]) => void;
  hydratePage: (snap: FollowingSnapshot, settings: FollowingSettings | null) => void;
  saveSettings: (next: FollowingSettings) => Promise<void>;
  applyDelta: (seq: number, d: OrgDelta) => void;
  markSeen: (ids: string[]) => void;
  applyLive: (seq: number, d: MeetingDelta) => void;
  liveSnapshot: (snap: MeetingSnapshot) => void;
}

export const DEFAULT_SETTINGS: FollowingSettings = {
  digest: true,
  alerts: { agendaListsTopics: true, topicsInTranscript: true, referredToCommittees: false },
};

export const matchKey = (m: Match) => m.id ?? `${m.meetingId}:${m.followId}:${m.label}`;

function index(matches: Match[]) {
  const byId: Record<string, Match> = {};
  const byMeeting: Record<string, string[]> = {};
  for (const m of matches) {
    const id = matchKey(m);
    byId[id] = m;
    (byMeeting[m.meetingId] ??= []).push(id);
  }
  return { byId, byMeeting };
}

export const followingStore = createStore<FollowingState>()(
  subscribeWithSelector((set, get) => ({
    follows: [],
    matchesById: {},
    matchIdsByMeeting: {},
    meetingOrder: [],
    catchUp: [],
    sharedNotes: [],
    seenIds: {},
    seq: 0,
    catchUpRange: "",
    meetings: [],
    othersLabel: null,
    people: {},
    legislation: [],
    person: null,
    topics: [],
    topicsAside: null,
    settings: DEFAULT_SETTINGS,
    live: null,

    hydratePage: (snap, settings) => {
      const { byId, byMeeting } = index(snap.matches);
      set({
        matchesById: byId,
        matchIdsByMeeting: byMeeting,
        meetingOrder: snap.meetings.map((m) => m.id),
        catchUp: snap.catchUp.lines,
        catchUpRange: snap.catchUp.range,
        meetings: snap.meetings,
        othersLabel: snap.othersLabel ?? null,
        people: snap.people,
        legislation: snap.legislation ?? [],
        person: snap.person ?? null,
        topics: snap.topics ?? [],
        topicsAside: snap.topicsAside ?? null,
        settings: settings ?? DEFAULT_SETTINGS,
        seq: snap.seq,
        live: snap.live ?? null,
      });
    },

    saveSettings: async (next) => {
      const prev = get().settings;
      set({ settings: next });
      const res = await fetch("/api/dashboard/settings", {
        method: "PUT", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(next),
      }).catch(() => null);
      if (!res?.ok) { set({ settings: prev }); toastStore.getState().show({ message: "Couldn't save that. Try again." }); }
    },

    hydrate: (snap, meetingOrder) => {
      const { byId, byMeeting } = index(snap.matches);
      set({
        follows: snap.follows,
        matchesById: byId,
        matchIdsByMeeting: byMeeting,
        meetingOrder,
        catchUp: snap.catchUp,
        sharedNotes: snap.sharedNotes,
        seq: snap.seq,
      });
    },

    applyDelta: (seq, d) => {
      if (seq <= get().seq) return;
      switch (d.t) {
        case "match.add": {
          const id = matchKey(d.match);
          set((s) => ({
            seq,
            matchesById: { ...s.matchesById, [id]: d.match },
            matchIdsByMeeting: {
              ...s.matchIdsByMeeting,
              [d.match.meetingId]: [...(s.matchIdsByMeeting[d.match.meetingId] ?? []), id],
            },
            meetingOrder: s.meetingOrder.includes(d.match.meetingId) ? s.meetingOrder : [d.match.meetingId, ...s.meetingOrder],
          }));
          break;
        }
        case "match.seen":
          set((s) => ({ seq, seenIds: { ...s.seenIds, ...Object.fromEntries(d.ids.map((i) => [i, true as const])) } }));
          break;
        case "follow.add":
          set((s) => ({ seq, follows: [...s.follows.filter((f) => f.id !== d.follow.id), d.follow] }));
          break;
        case "follow.remove":
          set((s) => ({ seq, follows: s.follows.filter((f) => f.id !== d.id) }));
          break;
        case "catchup.set":
          set({ seq, catchUp: d.lines });
          break;
        case "note.shared":
          set((s) => ({ seq, sharedNotes: [...s.sharedNotes, d.note] }));
          break;
      }
    },

    markSeen: (ids) =>
      set((s) => ({ seenIds: { ...s.seenIds, ...Object.fromEntries(ids.map((i) => [i, true as const])) } })),

    // Live now: deltas from the live meeting's MeetingRoom (meeting:{id}). A new speaker turn moves the
    // face; the meeting leaving "live" ends it. Replays below the snapshot's seq are ignored.
    applyLive: (seq, d) => {
      const live = get().live;
      if (!live || seq <= (live.seq ?? 0)) return;
      if (d.t === "turn.append") set({ live: { ...live, seq, speakerId: d.turn.personId } });
      else if (d.t === "status" && d.status !== "live") set({ live: null });
      else set({ live: { ...live, seq } });
    },
    liveSnapshot: (snap) => {
      const live = get().live;
      if (!live) return;
      if (snap.meeting.status !== "live") { set({ live: null }); return; }
      const turn = snap.turns[snap.turns.length - 1];
      set({ live: { ...live, seq: snap.seq, speakerId: turn?.personId ?? live.speakerId ?? null } });
    },
  })),
);

export const useFollowing = <T,>(selector: (s: FollowingState) => T) => useStore(followingStore, selector);

/** Number of matches flagged new by the server and not yet seen locally (drives the "N new" badge). */
export const selectNewCount = (s: FollowingState) =>
  Object.entries(s.matchesById).filter(([id, m]) => m.isNew && !s.seenIds[id]).length;
